import { skipToken, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import type {
  AdPlatform,
  AssessmentResponse,
  BrandProfileInput,
  AdAnalysisSummary,
  GetAdAccountsResponse,
  GetCompetitorsResponse,
  FunnelSummary,
  PutAdAccountsRequest,
  PutCompetitorsRequest,
  SessionSnapshot,
  StepState,
} from '@/contracts/onboarding-session';
import { HttpError } from '@/features/onboarding/services/http-client';

import type { DataState } from '../state/buildTranscript';
import { isTerminalState } from '../state/jobState';
import {
  getAdAccounts,
  getAdAnalysis,
  getAssessment,
  getBrandGuide,
  getCompetitors,
  getFunnel,
  postQuiz,
  putAdAccounts,
  putCompetitors,
} from '../services/onboarding-session.service';
import { onboardingKeys } from './queryKeys';
import { withRecovery, type RecoverSession } from './useSessionRecovery';

/** Slow re-read while something upstream is still working (the snapshot poll drives the fast one). */
const SETTLING_POLL_MS = 3000;
/** Slow re-read of an ad analysis that is `partial` but not `final`: a late profile can still make it `done`. */
const PARTIAL_ANALYSIS_POLL_MS = 15_000;

/** The BFF's 400 for an ad account or competitor id that is not in what it discovered. */
function isUnknownId(error: unknown): boolean {
  const code = error instanceof HttpError ? error.envelope?.error?.code : undefined;
  return code === 'unknown_ad_account' || code === 'unknown_competitor';
}

/**
 * A read that failed is final for the flow: the transcript leaves that step out, so a late success (a poll, a
 * refocus, a reconnect) must not bring the step back. The settling poll and the window refetches stop once the
 * query is in error with nothing to show (a failed refresh on top of data keeps going); a remount or an explicit invalidation (a new BFF session instance) still re-reads.
 */
const notAfterError = (query: { state: { status: string; data: unknown } }): boolean =>
  !(query.state.status === 'error' && query.state.data === undefined);

/** Maps a query to the transcript's DataState. */
function toDataState<T>(q: { data: T | undefined; isError: boolean; isPending: boolean; fetchStatus: string }): DataState<T> {
  if (q.data !== undefined) return { state: 'ready', data: q.data };
  if (q.isError) return { state: 'error' };
  return q.isPending && q.fetchStatus === 'idle' ? { state: 'idle' } : { state: 'loading' };
}

/**
 * GET /brand-guide, re-read while the site or research job is still working, and once more when the last of them
 * ends: the poll stops right there, and its last read may predate what that job found.
 */
export function useBrandGuide(sessionId: string, enabled: boolean, snapshot: SessionSnapshot | null, recover?: RecoverSession) {
  const settling = (snapshot?.jobs ?? []).some(
    (j) => (j.kind === 'siteProfile' || j.kind === 'research') && !isTerminalState(j.state),
  );
  const query = useQuery({
    queryKey: onboardingKeys.brandGuide(sessionId),
    queryFn: () => withRecovery(() => getBrandGuide(sessionId), recover),
    enabled,
    retry: 1,
    refetchInterval: settling ? SETTLING_POLL_MS : false,
  });
  const { refetch } = query;
  const wasSettling = useRef(settling);
  useEffect(() => {
    if (wasSettling.current && !settling) void refetch();
    wasSettling.current = settling;
  }, [settling, refetch]);
  return query;
}

function platformsActive(data: GetAdAccountsResponse | undefined): boolean {
  return !data || data.platforms.some((p) => p.status !== 'unsupported' && !isTerminalState(p.status as StepState));
}

/** GET /ad-accounts, re-read while a platform is still discovering ads. */
export function useAdAccounts(sessionId: string, enabled: boolean, recover?: RecoverSession): DataState<GetAdAccountsResponse> {
  const q = useQuery({
    queryKey: onboardingKeys.adAccounts(sessionId),
    queryFn: () => withRecovery(() => getAdAccounts(sessionId), recover),
    enabled,
    retry: 1,
    refetchInterval: (query) => (notAfterError(query) && platformsActive(query.state.data) ? SETTLING_POLL_MS : false),
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/** How long to wait before re-reading an analysis, or false when the BFF will not move the job any more. */
function analysisPollMs(data: AdAnalysisSummary | undefined): number | false {
  if (!data) return SETTLING_POLL_MS;
  if (data.final) return false;
  return data.state === 'partial' ? PARTIAL_ANALYSIS_POLL_MS : SETTLING_POLL_MS;
}

/**
 * GET /ads/:platform/analysis, re-read every few seconds while the analysis is `queued` (ads still being found)
 * or `running`, every `PARTIAL_ANALYSIS_POLL_MS` while it is `partial` and not yet `final`, and stopped once the
 * BFF says it is `final`. The route also moves the BFF's analysis job, so this poll is what advances it. A failed read with nothing to show is final for the card (it
 * says the analysis is unavailable); a failed refresh on top of data keeps going.
 */
export function useAdAnalysis(
  sessionId: string,
  platform: AdPlatform,
  enabled: boolean,
  recover?: RecoverSession,
): DataState<AdAnalysisSummary> {
  const q = useQuery({
    queryKey: onboardingKeys.adAnalysis(sessionId, platform),
    queryFn: () => withRecovery(() => getAdAnalysis(sessionId, platform), recover),
    enabled,
    retry: 1,
    refetchInterval: (query) => (notAfterError(query) ? analysisPollMs(query.state.data) : false),
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/**
 * The ad analysis as the cache holds it, with no request of its own: the ads card owns the poll (and only mounts
 * once the ad step is reached, so nothing here starts the BFF's analysis early). The Tasks panel reads its
 * `coverage` from here.
 */
export function useCachedAdAnalysis(sessionId: string, platform: AdPlatform): DataState<AdAnalysisSummary> {
  const q = useQuery<AdAnalysisSummary>({
    queryKey: onboardingKeys.adAnalysis(sessionId, platform),
    queryFn: skipToken,
  });
  return toDataState(q);
}

/** Most slow re-reads of a funnel whose scorecard failed in passing: the BFF retries it on later GETs, up to a cap of its own. */
const SCORECARD_RETRY_POLLS_MAX = 5;
/** A read that starts this much sooner than the slow poll after the previous one came from a focus, a reconnect, a mount or an invalidation, not from the poll. */
const POLL_TIMER_SLACK_MS = 1000;

/** A ready result whose primary funnel (the first) has no score because the scorecard call failed (not "not evaluated"). */
function scorecardFailed(data: FunnelSummary | undefined): boolean {
  if (data?.state !== 'ready') return false;
  const score = data.funnels[0]?.score;
  return score !== undefined && score.value === null && score.status === 'unavailable';
}

/** The primary funnel's score value, or null when the funnel is not ready or has no score yet. */
function primaryScore(data: FunnelSummary | undefined): number | null {
  return data?.state === 'ready' ? (data.funnels[0]?.score.value ?? null) : null;
}

/**
 * Retry polls spent on this session's failed scorecard. The count lives in the query cache under the session's key (not in a
 * per-mount ref), so a remount does not give the budget back and another session starts from zero.
 */
const scorecardPollsSpent = (client: QueryClient, sessionId: string): number =>
  client.getQueryData<number>(onboardingKeys.funnelScorecardPolls(sessionId)) ?? 0;

/**
 * The read that just came back moves the count: a ready result with the scorecard down spends one retry when the read was the
 * first to find it down (the previous result was running, absent or fine) or the slow poll (started a poll's length after the
 * previous one; a focus, reconnect, mount or invalidation read comes sooner, since every read restarts the poll's timer). A
 * result with a score, or with no failure, resets the count.
 */
function noteFunnelRead(
  client: QueryClient,
  sessionId: string,
  data: FunnelSummary,
  previous: { data: FunnelSummary | undefined; readAt: number },
): void {
  const key = onboardingKeys.funnelScorecardPolls(sessionId);
  if (!scorecardFailed(data)) {
    if (client.getQueryData(key) !== undefined) client.setQueryData(key, 0);
    return;
  }
  const wasPoll = !scorecardFailed(previous.data) || Date.now() - previous.readAt >= PARTIAL_ANALYSIS_POLL_MS - POLL_TIMER_SLACK_MS;
  if (wasPoll) client.setQueryData(key, scorecardPollsSpent(client, sessionId) + 1);
}

/**
 * GET /funnel, re-read every few seconds while FA is `running` (the route is what reads FA's progress). A `ready` result
 * whose primary funnel's scorecard failed in passing (`score.status === 'unavailable'`, no value) is re-read at the
 * slow `PARTIAL_ANALYSIS_POLL_MS` pace, at most `SCORECARD_RETRY_POLLS_MAX` times per session, since the BFF retries the
 * scorecard on later reads. Only the retry polls spend that budget (not a window-focus, reconnect or invalidation read),
 * and it is kept per session in the query cache, so it survives a remount. Otherwise `ready` and `unavailable` end the poll:
 * neither moves again. A failed read with nothing to show is final for the card (it says the funnels are unavailable); a
 * failed refresh on top of data keeps going.
 */
export function useFunnel(sessionId: string, enabled: boolean, recover?: RecoverSession): DataState<FunnelSummary> {
  const client = useQueryClient();
  const q = useQuery({
    queryKey: onboardingKeys.funnel(sessionId),
    queryFn: async () => {
      const before = client.getQueryState<FunnelSummary>(onboardingKeys.funnel(sessionId));
      const data = await withRecovery(() => getFunnel(sessionId), recover);
      noteFunnelRead(client, sessionId, data, { data: before?.data, readAt: before?.dataUpdatedAt ?? 0 });
      return data;
    },
    enabled,
    retry: 1,
    refetchInterval: (query) => {
      if (!notAfterError(query)) return false;
      const data = query.state.data;
      if (!data || data.state === 'running') return SETTLING_POLL_MS;
      return scorecardFailed(data) && scorecardPollsSpent(client, sessionId) < SCORECARD_RETRY_POLLS_MAX ? PARTIAL_ANALYSIS_POLL_MS : false;
    },
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/** The assessment read, as the page tells it apart: not asked yet, loading, ready, "not ready" (the quiz is not stored) or failed. */
export type AssessmentState = DataState<AssessmentResponse> | { state: 'not_ready' };

/** The BFF's 409 `not_ready`: the assessment was asked for before the quiz was submitted. Not a failure, a state of its own. */
export function isAssessmentNotReady(error: unknown): boolean {
  return error instanceof HttpError && error.status === 409 && error.envelope?.error?.code === 'not_ready';
}

/** Retries a failed read once, except a 409 `not_ready`: asking again would not change the answer. */
const assessmentRetry = (failureCount: number, error: unknown): boolean => !isAssessmentNotReady(error) && failureCount < 1;

/**
 * GET /assessment. A 409 `not_ready` is its own state (`'not_ready'`, never an error to toast); any other failure with
 * nothing to show is `'error'`. It does not poll: it is read when asked, and again by `useRefetchAssessmentWhenFunnelSettles`.
 */
export function useAssessment(sessionId: string, enabled: boolean, recover?: RecoverSession): AssessmentState {
  const q = useQuery({
    queryKey: onboardingKeys.assessment(sessionId),
    queryFn: () => withRecovery(() => getAssessment(sessionId), recover),
    enabled,
    retry: assessmentRetry,
    // As its siblings: a failed read with nothing to show is final until a remount or an invalidation.
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  if (q.data === undefined && isAssessmentNotReady(q.error)) return { state: 'not_ready' };
  return toDataState(q);
}

/**
 * The assessment as the cache holds it, with no request of its own: the assessment page owns the read. The Tasks panel
 * shows its "Growth Score" row done once this is `ready` (the route answered 200).
 */
export function useCachedAssessment(sessionId: string): DataState<AssessmentResponse> {
  const q = useQuery<AssessmentResponse>({
    queryKey: onboardingKeys.assessment(sessionId),
    queryFn: skipToken,
  });
  return toDataState(q);
}

/**
 * While the funnel job runs, the assessment's Funnel dimension is `example` (the BFF has no scorecard yet). So the
 * assessment is read again once, when the funnel read leaves "not settled yet" (no answer yet, or `running`): `ready`,
 * `unavailable` and a failed read all settle it. It is read again, too, when the primary funnel's score goes from none to a
 * number (the scorecard that failed in passing came back on a later read). Both at once read it once. Reads the funnel with
 * the same shared query the chat's funnel card uses.
 */
export function useRefetchAssessmentWhenFunnelSettles(sessionId: string, enabled: boolean, recover?: RecoverSession): void {
  const client = useQueryClient();
  const funnel = useFunnel(sessionId, enabled, recover);
  const settled = funnel.state === 'error' || (funnel.state === 'ready' && funnel.data.state !== 'running');
  const score = funnel.state === 'ready' ? primaryScore(funnel.data) : null;
  const wasSettled = useRef(settled);
  const wasScored = useRef(score !== null);
  useEffect(() => {
    const justSettled = !wasSettled.current && settled;
    const justScored = !wasScored.current && score !== null;
    if (justSettled || justScored) void client.invalidateQueries({ queryKey: onboardingKeys.assessment(sessionId) });
    wasSettled.current = settled;
    wasScored.current = score !== null;
  }, [settled, score, client, sessionId]);
}

/** GET /competitors, re-read while the research workflow has not produced a shortlist. */
export function useCompetitors(sessionId: string, enabled: boolean, recover?: RecoverSession): DataState<GetCompetitorsResponse> {
  const q = useQuery({
    queryKey: onboardingKeys.competitors(sessionId),
    queryFn: () => withRecovery(() => getCompetitors(sessionId), recover),
    enabled,
    retry: 1,
    refetchInterval: (query) => {
      if (!notAfterError(query)) return false;
      const data = query.state.data;
      return !data || (!isTerminalState(data.status) && data.competitors.length === 0) ? SETTLING_POLL_MS : false;
    },
    refetchOnWindowFocus: notAfterError,
    refetchOnReconnect: notAfterError,
  });
  return toDataState(q);
}

/**
 * POST /quiz at quiz-done. The BFF side is idempotent, so a repeat is harmless. No retry of its own: a failure
 * is shown and re-armed with backoff by `useQuizSubmit`; a lost session is recovered and retried once here.
 */
export function usePostQuiz(sessionId: string, recover?: RecoverSession) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (answers: BrandProfileInput) => withRecovery(() => postQuiz(sessionId, answers), recover),
    onSuccess: () => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(sessionId), (old) =>
        old ? { ...old, quizSubmitted: true } : old,
      );
    },
  });
}

/** PUT /ad-accounts/:platform; the BFF stores the confirmation and the snapshot picks it up. */
export function useConfirmAdAccounts(sessionId: string, recover?: RecoverSession) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ platform, body }: { platform: AdPlatform; body: PutAdAccountsRequest }) =>
      withRecovery(() => putAdAccounts(sessionId, platform, body), recover),
    // An id the BFF does not know (the list moved on): re-read the list so the panel offers what exists.
    onError: (error) => {
      if (isUnknownId(error)) void client.invalidateQueries({ queryKey: onboardingKeys.adAccounts(sessionId) });
    },
    onSuccess: (result) => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(sessionId), (old) =>
        old
          ? {
              ...old,
              confirmations: {
                ...old.confirmations,
                adAccounts: { ...old.confirmations.adAccounts, [result.platform]: result.confirmation },
              },
            }
          : old,
      );
      void client.invalidateQueries({ queryKey: onboardingKeys.adAccounts(sessionId) });
      // The BFF now follows the confirmed account: its analysis and the live-ad count (in the snapshot) moved.
      void client.invalidateQueries({ queryKey: onboardingKeys.adAnalysis(sessionId, result.platform) });
      void client.invalidateQueries({ queryKey: onboardingKeys.snapshot(sessionId) });
    },
  });
}

/** PUT /competitors. */
export function useConfirmCompetitors(sessionId: string, recover?: RecoverSession) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PutCompetitorsRequest) => withRecovery(() => putCompetitors(sessionId, body), recover),
    onError: (error) => {
      if (isUnknownId(error)) void client.invalidateQueries({ queryKey: onboardingKeys.competitors(sessionId) });
    },
    onSuccess: (result) => {
      client.setQueryData<SessionSnapshot>(onboardingKeys.snapshot(sessionId), (old) =>
        old ? { ...old, confirmations: { ...old.confirmations, competitors: result.confirmation } } : old,
      );
      void client.invalidateQueries({ queryKey: onboardingKeys.competitors(sessionId) });
    },
  });
}
